import { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LEA_AVATAR = "https://firebasestorage.googleapis.com/v0/b/studio-8303426965-f811a.firebasestorage.app/o/images%2FlandingPage%2FLEA.png?alt=media&token=e2b3d49e-25c4-4550-b2b7-f7e43660f73a";

export default function LeaChatbot() {
  const [isOpen, setIsOpen] = useState(false);
  const [exitShown, setExitShown] = useState(false);
  const [showExitPrompt, setShowExitPrompt] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', content: "Hello! I'm LEA, your online assistant for estate planning, SPA, and legacy protection. How can I help you today?\n\nI can answer in English or Filipino - just let me know your preference!" }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const scrollStartY = useRef(0);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Exit intent - Desktop: mouse leaves viewport top
  const handleMouseLeave = useCallback((e) => {
    if (e.clientY <= 5 && !exitShown && !isOpen) {
      const dismissed = sessionStorage.getItem('ls_exit_dismissed');
      if (!dismissed) {
        setShowExitPrompt(true);
        setExitShown(true);
      }
    }
  }, [exitShown, isOpen]);

  // Exit intent - Mobile: rapid scroll up
  const handleTouchStart = useCallback((e) => {
    scrollStartY.current = e.touches[0].clientY;
  }, []);

  const handleTouchMove = useCallback((e) => {
    const diff = e.touches[0].clientY - scrollStartY.current;
    if (diff > 80 && window.scrollY < 200 && !exitShown && !isOpen) {
      const dismissed = sessionStorage.getItem('ls_exit_dismissed');
      if (!dismissed) {
        setShowExitPrompt(true);
        setExitShown(true);
      }
    }
  }, [exitShown, isOpen]);

  useEffect(() => {
    document.addEventListener('mouseleave', handleMouseLeave);
    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: true });
    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
    };
  }, [handleMouseLeave, handleTouchStart, handleTouchMove]);

  const dismissExit = () => {
    setShowExitPrompt(false);
    sessionStorage.setItem('ls_exit_dismissed', 'true');
  };

  const openFromExit = () => {
    setShowExitPrompt(false);
    setIsOpen(true);
    sessionStorage.setItem('ls_exit_dismissed', 'true');
  };

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/chat`, { message: userMsg, session_id: sessionId });
      setSessionId(res.data.session_id);
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.reply }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: "I'm sorry, I'm having trouble connecting. Please visit legasecure.com for immediate help." }]);
    }
    setLoading(false);
  };

  return (
    <>
      {/* Exit Intent Popup */}
      {showExitPrompt && !isOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center" data-testid="exit-intent-popup">
          <div className="absolute inset-0 bg-black/40" onClick={dismissExit}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl w-[400px] max-w-[90vw] p-6 text-center">
            <button onClick={dismissExit} className="absolute top-3 right-3 text-[var(--ls-text-light)] hover:text-[var(--ls-navy)]">
              <i className="fa-solid fa-xmark text-lg"></i>
            </button>
            <img src={LEA_AVATAR} alt="LEA" className="w-16 h-16 rounded-full mx-auto mb-3 border-2 border-[var(--ls-gold)]" />
            <h3 className="text-lg font-bold text-[var(--ls-navy)] font-['Crimson_Pro'] mb-2">Wait — before you go!</h3>
            <p className="text-sm text-[var(--ls-text-light)] mb-4">
              I'm LEA, and I can help you with estate planning, SPA, or protecting your family's future. Do you have any questions I can answer?
            </p>
            <div className="flex gap-3 justify-center">
              <button onClick={openFromExit} className="ls-btn-navy text-sm px-6 py-2.5" data-testid="exit-chat-btn">
                <i className="fa-solid fa-comment mr-2"></i>Chat with LEA
              </button>
              <a href="https://legasecure.com" className="ls-btn-outline text-sm px-6 py-2.5" data-testid="exit-visit-btn">
                Visit LegaSecure
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Chat Toggle Button */}
      {!isOpen && !showExitPrompt && (
        <button
          data-testid="lea-chat-toggle" aria-label="Open chat with LEA assistant"
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-50 group"
        >
          <div className="relative">
            <div className="absolute inset-0 rounded-full bg-[var(--ls-gold)] animate-ping opacity-30"></div>
            <div className="relative flex items-center gap-3 bg-[var(--ls-navy)] text-white pl-2 pr-5 py-2 rounded-full shadow-xl hover:bg-[var(--ls-navy-dark)] transition-all hover:scale-105">
              <img src={LEA_AVATAR} alt="LEA" className="w-12 h-12 rounded-full object-cover border-2 border-[var(--ls-gold)]" />
              <div className="hidden sm:block text-left">
                <p className="text-sm font-bold leading-tight">LEA</p>
                <p className="text-[10px] text-white/70 leading-tight">ONLINE ASSISTANT</p>
              </div>
            </div>
          </div>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div data-testid="lea-chat-window" className="fixed bottom-0 right-0 sm:bottom-6 sm:right-6 z-50 w-full sm:w-[400px] sm:max-w-[calc(100vw-2rem)] bg-white sm:rounded-2xl shadow-2xl border border-[var(--ls-border)] flex flex-col" style={{ height: 'calc(100vh - 60px)', maxHeight: '520px' }}>
          <div className="flex items-center gap-3 px-4 py-3 bg-[var(--ls-bg-light)] rounded-t-2xl border-b border-[var(--ls-border)]">
            <img src={LEA_AVATAR} alt="LEA" className="w-10 h-10 rounded-full object-cover" />
            <div className="flex-1">
              <p className="font-bold text-sm text-[var(--ls-navy)]">LEA</p>
              <p className="text-xs text-[var(--ls-text-light)]">ONLINE ASSISTANT</p>
            </div>
            <button data-testid="lea-chat-close" aria-label="Close chat" onClick={() => setIsOpen(false)} className="text-[var(--ls-text-light)] hover:text-[var(--ls-navy)] text-lg">
              <i className="fa-solid fa-xmark"></i>
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                {msg.role === 'assistant' && <img src={LEA_AVATAR} alt="LEA" className="w-7 h-7 rounded-full mr-2 mt-1 flex-shrink-0" />}
                <div className={`max-w-[75%] px-3 py-2 rounded-xl text-sm leading-relaxed ${
                  msg.role === 'user'
                    ? 'bg-[var(--ls-navy)] text-white rounded-br-sm'
                    : 'bg-[var(--ls-bg)] text-[var(--ls-text)] rounded-bl-sm'
                }`}>
                  {msg.content.split('\n').map((line, j) => (
                    <p key={j} className={j > 0 ? 'mt-1' : ''}>{line}</p>
                  ))}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <img src={LEA_AVATAR} alt="LEA" className="w-7 h-7 rounded-full mr-2 mt-1" />
                <div className="bg-[var(--ls-bg)] px-4 py-3 rounded-xl rounded-bl-sm">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 rounded-full bg-[var(--ls-text-light)] animate-bounce" style={{ animationDelay: '0ms' }}></span>
                    <span className="w-2 h-2 rounded-full bg-[var(--ls-text-light)] animate-bounce" style={{ animationDelay: '150ms' }}></span>
                    <span className="w-2 h-2 rounded-full bg-[var(--ls-text-light)] animate-bounce" style={{ animationDelay: '300ms' }}></span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="px-4 py-3 border-t border-[var(--ls-border)]">
            <div className="flex gap-2">
              <input
                data-testid="lea-chat-input" aria-label="Type your message to LEA"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && sendMessage()}
                placeholder="Type your message..."
                className="flex-1 px-3 py-2 rounded-full bg-[var(--ls-bg)] text-sm border border-[var(--ls-border)] focus:outline-none focus:border-[var(--ls-gold)]"
              />
              <button
                data-testid="lea-chat-send" aria-label="Send message"
                onClick={sendMessage}
                disabled={loading || !input.trim()}
                className="w-9 h-9 rounded-full bg-[var(--ls-navy)] text-white flex items-center justify-center hover:bg-[var(--ls-gold)] transition-colors disabled:opacity-50"
              >
                <i className="fa-solid fa-paper-plane text-xs"></i>
              </button>
            </div>
            <p className="text-[10px] text-[var(--ls-text-light)] mt-2 text-center">
              LEA is an AI assistant, not a lawyer. By chatting, you acknowledge LegaSecure's{' '}
              <a href="https://legasecure.com/privacy-policy" className="underline">Privacy Policy</a>.
            </p>
          </div>
        </div>
      )}
    </>
  );
}
